import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children, tvMode = false }) {
  const auth = useAuth();
  const user = auth?.user;
  const [lastEvent, setLastEvent] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const listenersRef = useRef(new Set());

  const connect = useCallback(() => {
    const token = localStorage.getItem("ceap_token");
    if (!token && !tvMode) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws?${token ? `token=${token}` : ""}${tvMode ? "&tv=1" : ""}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          setLastEvent({ ...data, _ts: Date.now() });
          listenersRef.current.forEach((cb) => { try { cb(data); } catch {} });
          // Iter 47: dispara evento no window pra que Layout/NotificationBell
          // possam escutar sem depender do lastEvent (que só emite último).
          if (data?.event) {
            try {
              window.dispatchEvent(new CustomEvent(`ws:${data.event}`, { detail: data }));
            } catch {}
          }
        } catch {}
      };
    } catch {}
  }, [tvMode]);

  useEffect(() => {
    if (!user && !tvMode) return;
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user, tvMode, connect]);

  const subscribe = (cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  };

  return (
    <RealtimeContext.Provider value={{ lastEvent, connected, subscribe }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
